// Builds a single, self-contained HTML file (inline CSS + vanilla JS, no backend calls)
// that reproduces the Client View flow + phase detail panel so it can be opened and
// clicked through outside the app. Presentation data is embedded as a plain JS array.
import { escapeHtml as esc } from './escapeHtml'

const PALETTE = {
  pink: { c: '#E91E8C', light: '#FCE4EC' },
  blue: { c: '#1565C0', light: '#E3F2FD' },
  green: { c: '#2E7D32', light: '#E8F5E9' },
  orange: { c: '#F57C00', light: '#FFF3E0' },
  purple: { c: '#6A1B9A', light: '#F3E5F5' },
  teal: { c: '#00897B', light: '#E0F2F1' },
  slate: { c: '#37474F', light: '#ECEFF1' },
  amber: { c: '#F9A825', light: '#FFF8E1' }
}
const TYPE_LABELS = {
  gate: 'Entry / Gate',
  onboarding: 'Onboarding',
  education: 'Education',
  intent: 'Intent',
  barrier: 'Barrier',
  screening: 'Screening',
  referral: 'Referral',
  conversion: 'Conversion',
  exit: 'Exit'
}

// Same BFS-depth grouping used on-canvas (CreateNewJourney.vue's computePhaseLayout),
// reproduced here so the exported page fans branches out into rows without needing
// the app's canvas/pan/zoom machinery.
function computeRows (data) {
  const phases = data.phases || []
  const ids = phases.map(p => p.id)
  const idSet = new Set(ids)
  const happy = data.happy_path || []
  const edgeSet = new Set()
  const edges = []
  function addEdge (f, t) {
    if (!idSet.has(f) || !idSet.has(t) || f === t) return
    const k = f + '->' + t
    if (edgeSet.has(k)) return
    edgeSet.add(k); edges.push([f, t])
  }
  for (let i = 0; i < happy.length - 1; i++) addEdge(happy[i], happy[i + 1])
  phases.forEach(p => { (p.branches_to || []).forEach(b => addEdge(p.id, b.target_phase)) })

  const backEdges = new Set()
  const visited = new Set()
  const inStack = new Set()
  function dfs (n) {
    visited.add(n); inStack.add(n)
    edges.forEach(([f, t]) => {
      if (f !== n) return
      if (inStack.has(t)) { backEdges.add(f + '->' + t); return }
      if (!visited.has(t)) dfs(t)
    })
    inStack.delete(n)
  }
  const root = (happy[0] && idSet.has(happy[0])) ? happy[0] : ids[0]
  if (root) dfs(root)
  ids.forEach(n => { if (!visited.has(n)) dfs(n) })

  const fwdAdj = {}
  ids.forEach(n => { fwdAdj[n] = [] })
  edges.forEach(([f, t]) => { if (!backEdges.has(f + '->' + t)) fwdAdj[f].push(t) })

  const depth = {}
  ids.forEach(n => { depth[n] = -1 })
  if (root) {
    depth[root] = 0
    const queue = [root]
    while (queue.length) {
      const n = queue.shift()
      fwdAdj[n].forEach(t => { if (depth[t] < depth[n] + 1) { depth[t] = depth[n] + 1; queue.push(t) } })
    }
  }
  const maxD = Math.max(...Object.values(depth).filter(d => d >= 0), 0)
  ids.forEach(n => { if (depth[n] < 0) depth[n] = maxD + 1 })

  const rowsByDepth = {}
  ids.forEach(n => { const d = depth[n]; if (!rowsByDepth[d]) rowsByDepth[d] = []; rowsByDepth[d].push(n) })
  return Object.keys(rowsByDepth).map(Number).sort((a, b) => a - b).map(d => rowsByDepth[d])
}

function chip (color) {
  const pal = PALETTE[color] || PALETTE.slate
  return `background:${pal.light};color:${pal.c};border-color:${pal.c}`
}

function nodeHtml (phase, byId, happySet) {
  const pal = PALETTE[phase.color] || PALETTE.slate
  const stageCount = (phase.stages || []).length
  const branches = phase.branches_to || []
  const branchesHtml = branches.length
    ? `<div class="node-branches">${branches.map(b => `
        <span class="node-branch-pill" style="${chip(byId[b.target_phase] ? byId[b.target_phase].color : 'slate')}" title="${esc(b.condition || '')}">
          → ${esc(byId[b.target_phase] ? byId[b.target_phase].label : b.target_phase)}
        </span>`).join('')}</div>`
    : ''
  return `
    <div class="node" style="border-color:${pal.c}" onclick="showPhase('${esc(phase.id)}')" id="node-${esc(phase.id)}">
      <div class="node-head" style="background:${pal.light};color:${pal.c}">
        <span>${esc(TYPE_LABELS[phase.type] || phase.type || '')}</span>
        ${happySet.has(phase.id) ? '<span class="node-happy" title="On the main happy path">★</span>' : ''}
      </div>
      <div class="node-title">${esc(phase.label)}</div>
      <div class="node-desc">${esc(phase.description || '')}</div>
      <div class="node-count">${stageCount} stage${stageCount === 1 ? '' : 's'}</div>
      ${branchesHtml}
    </div>`
}

function detailPanelDefaultHtml (data) {
  const phases = data.phases || []
  let html = ''
  if (data.summary) html += `<div class="pe-summary">${esc(data.summary)}</div>`
  html += `<div class="pe-stages-head">Phases · ${phases.length}</div><div class="pe-list">`
  phases.forEach(p => {
    html += `<div class="pe-card" onclick="showPhase('${esc(p.id)}')">
      <div class="pe-name">${esc(p.label)}</div>
      <div class="pe-meta">
        <span class="pe-badge" style="${chip(p.color)}">${esc(TYPE_LABELS[p.type] || p.type || '')}</span>
        <span class="pe-desc">${(p.stages || []).length} stage${(p.stages || []).length === 1 ? '' : 's'}</span>
      </div>
    </div>`
  })
  html += '</div>'
  if (data.loops && data.loops.length) {
    html += `<div class="pe-detail-section"><div class="pe-detail-h">Loops</div>`
    data.loops.forEach(loop => {
      html += `<div class="pe-loop-item">
        <span class="pe-phase-chip" onclick="showPhase('${esc(loop.from_phase)}')">${esc(loop.from_phase)}</span>
        <span class="pe-loop-arrow">↻</span>
        <span class="pe-phase-chip" onclick="showPhase('${esc(loop.to_phase)}')">${esc(loop.to_phase)}</span>
        <div class="pe-loop-trigger">${esc(loop.trigger || '')}</div>
      </div>`
    })
    html += '</div>'
  }
  if (data.exit_points_summary && data.exit_points_summary.length) {
    html += `<div class="pe-detail-section"><div class="pe-detail-h">Exit Points</div>`
    data.exit_points_summary.forEach(ep => {
      html += `<div class="pe-exit-item"><span class="pe-mono">${esc(ep.stage)}</span> — ${esc(ep.reason)}</div>`
    })
    html += '</div>'
  }
  return html
}

export function buildClientViewHtml (journeyLabel, presentationData) {
  const data = presentationData || {}
  const phases = data.phases || []
  const byId = {}
  phases.forEach(p => { byId[p.id] = p })
  const happySet = new Set(data.happy_path || [])
  const rows = computeRows(data)

  const rowsHtml = rows.map(row => `
    <div class="flow-row${row.length > 1 ? ' fan' : ''}">
      ${row.map(id => byId[id] ? nodeHtml(byId[id], byId, happySet) : '').join('')}
    </div>
    <div class="row-arrow"></div>
  `).join('')

  const defaultDetail = detailPanelDefaultHtml(data)

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${esc(journeyLabel)} — Client View</title>
<style>
* { box-sizing: border-box; margin: 0; padding: 0; }
:root{
  --card:#ffffff;--bg:#F0F4F8;--border:#DDE3EA;--text:#16233A;--muted:#5D6C7E;--faint:#94A2B1;--accent:#0D9488;
}
body{font-family:'DM Sans',Inter,system-ui,sans-serif;background:var(--bg);color:var(--text);min-height:100vh}
.site-header{
  background:var(--card);border-bottom:1px solid var(--border);padding:16px 28px;
  display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:100;
  box-shadow:0 2px 8px rgba(0,0,0,0.06);
}
.brand-title{font-size:14px;font-weight:700;color:var(--text)}
.brand-sub{font-size:11px;color:var(--muted);margin-top:2px}
.header-meta{font-size:11px;color:var(--faint);font-family:monospace;text-align:right}
.main{display:flex;min-height:calc(100vh - 61px)}

.flow-panel{flex:1;min-width:0;padding:32px 24px 80px;overflow-y:auto;display:flex;flex-direction:column;align-items:center}
.flow-row{display:flex;gap:24px;justify-content:center;flex-wrap:wrap;width:100%}
.flow-row.fan{padding:10px 0;border-top:2px dashed var(--border);border-bottom:2px dashed var(--border);margin:6px 0}
.row-arrow{width:2px;height:22px;background:var(--border)}
.row-arrow:last-child{display:none}

.node{
  width:230px;border:2px solid var(--border);border-radius:10px;background:var(--card);
  padding:12px 14px;cursor:pointer;transition:transform .12s,box-shadow .12s;
}
.node:hover{transform:translateY(-2px);box-shadow:0 6px 20px rgba(0,0,0,0.12)}
.node-head{display:flex;align-items:center;justify-content:space-between;font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;padding:2px 7px;border-radius:5px;margin-bottom:8px}
.node-happy{font-size:11px}
.node-title{font-size:13.5px;font-weight:700;line-height:1.3}
.node-desc{font-size:11.5px;color:var(--muted);margin-top:5px;line-height:1.45}
.node-count{font-size:10.5px;color:var(--faint);margin-top:8px;font-family:monospace}
.node-branches{margin-top:9px;display:flex;flex-direction:column;gap:4px}
.node-branch-pill{font-size:10px;font-weight:600;padding:2px 7px;border-radius:999px;border:1px solid;display:inline-block;width:fit-content}

.detail-panel{
  width:380px;min-width:380px;background:var(--card);border-left:1px solid var(--border);
  position:sticky;top:61px;height:calc(100vh - 61px);overflow-y:auto;padding:18px 18px 40px;
}
.pe-title{font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.05em;margin-bottom:12px}
.pe-summary{font-size:12px;line-height:1.6;color:var(--muted);background:#F6F8FA;border:1px solid var(--border);border-radius:8px;padding:10px 12px;margin-bottom:14px}
.pe-stages-head{font-size:10.5px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px}
.pe-list{display:flex;flex-direction:column;gap:5px}
.pe-card{padding:9px 10px;border:1px solid var(--border);border-radius:8px;cursor:pointer;transition:all .1s}
.pe-card:hover{border-color:var(--accent);background:#F6F8FA}
.pe-name{font-size:12.5px;font-weight:700}
.pe-meta{font-size:10.5px;color:var(--muted);margin-top:5px;display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.pe-badge{display:inline-flex;font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.03em;padding:2px 8px;border-radius:999px;border:1px solid}
.pe-back{display:inline-flex;align-items:center;gap:5px;background:none;border:none;color:var(--accent);cursor:pointer;font-size:12px;font-weight:600;padding:0;margin-bottom:12px}
.pe-back:hover{text-decoration:underline}
.pe-detail-name{font-size:15px;font-weight:700;margin-top:6px;margin-bottom:4px;line-height:1.3}
.pe-detail-desc{font-size:12px;color:var(--muted);line-height:1.55}
.pe-detail-section{margin-top:16px}
.pe-detail-h{font-size:10.5px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px}
.pe-stage-chips{display:flex;flex-wrap:wrap;gap:6px}
.pe-stage-chip{font-family:monospace;font-size:10px;color:var(--muted);background:#F6F8FA;border:1px solid var(--border);border-radius:5px;padding:2px 7px}
.pe-q-card{background:#F6F8FA;border:1px solid var(--border);border-radius:8px;padding:10px 11px;margin-bottom:8px}
.pe-q-stage{font-family:monospace;font-size:9.5px;color:var(--faint);margin-bottom:4px}
.pe-q-text{font-size:12px;font-weight:700;line-height:1.4}
.pe-q-opts{margin-top:6px;display:flex;flex-direction:column;gap:3px}
.pe-q-opt{font-size:11px;color:var(--muted);padding-left:12px;position:relative}
.pe-q-opt::before{content:'•';position:absolute;left:1px;color:var(--faint)}
.pe-branch-item{display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border);cursor:pointer}
.pe-branch-item:last-child{border-bottom:none}
.pe-branch-cond{font-size:11.5px;color:var(--muted)}
.pe-phase-chip{display:inline-block;font-size:10.5px;font-weight:700;border-radius:999px;padding:1px 8px;border:1px solid var(--border);background:#F6F8FA;white-space:nowrap;cursor:pointer}
.pe-loop-item{background:#F6F8FA;border:1px solid var(--border);border-radius:8px;padding:9px 10px;margin-bottom:7px;font-size:11.5px}
.pe-loop-arrow{margin:0 5px;color:var(--faint)}
.pe-loop-trigger{margin-top:5px;font-size:10.5px;color:var(--muted)}
.pe-exit-item{font-size:11.5px;color:var(--muted);padding:5px 0;border-bottom:1px solid var(--border)}
.pe-exit-item:last-child{border-bottom:none}
.pe-mono{font-family:monospace;font-size:10.5px}
@media (max-width: 900px){ .main{flex-direction:column} .detail-panel{width:100%;min-width:0;position:relative;top:0;height:auto;max-height:60vh} }
</style>
</head>
<body>
<header class="site-header">
  <div>
    <div class="brand-title">${esc(journeyLabel)}</div>
    <div class="brand-sub">Client View · ${phases.length} phase${phases.length === 1 ? '' : 's'} · ${esc(data.total_stages || '')} stages</div>
  </div>
  <div class="header-meta">Exported snapshot<br>Click a phase to see details</div>
</header>
<div class="main">
  <div class="flow-panel">${rowsHtml}</div>
  <div class="detail-panel" id="detail-panel">
    <div class="pe-title" id="pe-title">Client View</div>
    <div id="detail-body">${defaultDetail}</div>
  </div>
</div>
<script>
var PHASES = ${JSON.stringify(phases)};
var DEFAULT_HTML = document.getElementById('detail-body').innerHTML;
var PHASE_BY_ID = {};
PHASES.forEach(function (p) { PHASE_BY_ID[p.id] = p; });
var TYPE_LABELS = ${JSON.stringify(TYPE_LABELS)};

function escHtml (s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  });
}

function showPhase (id) {
  var p = PHASE_BY_ID[id];
  document.querySelectorAll('.node.selected').forEach(function (el) { el.classList.remove('selected'); });
  var nodeEl = document.getElementById('node-' + id);
  if (nodeEl) nodeEl.classList.add('selected');
  document.getElementById('pe-title').textContent = 'Phase Details';
  if (!p) return;
  var html = '<button class="pe-back" onclick="showPhaseList()">‹ Back to phases</button>' +
    '<div class="pe-detail-name">' + escHtml(p.label) + '</div>' +
    '<div class="pe-detail-desc">' + escHtml(p.description || '') + '</div>' +
    '<div class="pe-detail-section"><div class="pe-detail-h">Stages (' + (p.stages || []).length + ')</div>' +
    '<div class="pe-stage-chips">' + (p.stages || []).map(function (s) { return '<span class="pe-stage-chip">' + escHtml(s) + '</span>'; }).join('') + '</div></div>';
  if (p.key_questions && p.key_questions.length) {
    html += '<div class="pe-detail-section"><div class="pe-detail-h">Key Questions</div>';
    p.key_questions.forEach(function (q) {
      html += '<div class="pe-q-card"><div class="pe-q-stage">' + escHtml(q.stage) + '</div>' +
        '<div class="pe-q-text">' + escHtml(q.question_summary) + '</div>' +
        '<div class="pe-q-opts">' + (q.options_summary || []).map(function (o) { return '<div class="pe-q-opt">' + escHtml(o) + '</div>'; }).join('') + '</div></div>';
    });
    html += '</div>';
  }
  if (p.branches_to && p.branches_to.length) {
    html += '<div class="pe-detail-section"><div class="pe-detail-h">Branches To</div>';
    p.branches_to.forEach(function (b) {
      var target = PHASE_BY_ID[b.target_phase];
      html += '<div class="pe-branch-item" onclick="showPhase(\\'' + b.target_phase + '\\')">' +
        '<span class="pe-phase-chip">' + escHtml(target ? target.label : b.target_phase) + '</span>' +
        '<span class="pe-branch-cond">' + escHtml(b.condition || '') + '</span></div>';
    });
    html += '</div>';
  }
  if (p.exit_points && p.exit_points.length) {
    html += '<div class="pe-detail-section"><div class="pe-detail-h">Exit Points</div>';
    p.exit_points.forEach(function (ep) {
      html += '<div class="pe-exit-item"><span class="pe-mono">' + escHtml(ep.stage) + '</span> — ' + escHtml(ep.reason) + '</div>';
    });
    html += '</div>';
  }
  document.getElementById('detail-body').innerHTML = html;
}

function showPhaseList () {
  document.querySelectorAll('.node.selected').forEach(function (el) { el.classList.remove('selected'); });
  document.getElementById('pe-title').textContent = 'Client View';
  document.getElementById('detail-body').innerHTML = DEFAULT_HTML;
}
</script>
</body>
</html>`
}



// WEBPACK FOOTER //
// ./src/components/user-journey/journey-flow/exportClientViewHtml.js